import axios from "axios";

// If REACT_APP_API_URL is set to empty string, use relative URLs (same domain)
// If not set at all, default to relative URLs for backward compatibility
const API_URL =
  process.env.REACT_APP_API_URL !== undefined
    ? process.env.REACT_APP_API_URL
    : "";

// Create a new story
export const createStory = async (formData, token) => {
  try {
    const { data } = await axios.post(
      `${API_URL}/createStory`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    console.error("Error in createStory:", error);
    return error.response.data;
  }
};

// Get stories from followed users
export const getFollowingStories = async (token) => {
  try {
    const { data } = await axios.get(
      `${API_URL}/getFollowingStories`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response.data;
  }
};

// Get user's own stories
export const getUserStories = async (token) => {
  try {
    const { data } = await axios.get(
      `${API_URL}/getUserStories`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response.data;
  }
};

// View a specific story
export const viewStory = async (storyId, token) => {
  try {
    const { data } = await axios.put(
      `${API_URL}/viewStory/${storyId}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response.data;
  }
};

// Delete a story
export const deleteStory = async (storyId, token) => {
  try {
    const { data } = await axios.delete(
      `${API_URL}/deleteStory/${storyId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response.data;
  }
};

// Get story viewers
export const getStoryViewers = async (storyId, token) => {
  try {
    const { data } = await axios.get(
      `${API_URL}/getStoryViewers/${storyId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response.data;
  }
};

// Like/Unlike a story
export const likeStory = async (storyId, token) => {
  try {
    const { data } = await axios.put(
      `${API_URL}/likeStory/${storyId}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response?.data;
  }
};

// Get user's highlights
export const getUserHighlights = async (targetUserId, token) => {
  try {
    let url = `${API_URL}/getUserHighlights`;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    
    if (targetUserId) {
      // Pass as query parameter instead of path parameter for flexibility
      config.params = { userId: targetUserId };
    }
    
    const { data } = await axios.get(url, config);
    return data;
  } catch (error) {
    return error.response?.data;
  }
};

// Delete a highlight
export const deleteHighlight = async (storyId, token) => {
  try {
    const { data } = await axios.delete(
      `${API_URL}/deleteHighlight/${storyId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response?.data;
  }
};

// Update highlight cover image
export const updateHighlightCover = async (formData, token) => {
  try {
    const { data } = await axios.put(
      `${API_URL}/updateHighlightCover`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          // Don't set Content-Type - let axios set it automatically with boundary
        },
      }
    );
    return data;
  } catch (error) {
    console.error("Error in updateHighlightCover:", error);
    return error.response?.data;
  }
};

// Set cover from existing story
export const setCoverFromStory = async (highlightName, storyId, token) => {
  try {
    const { data } = await axios.put(
      `${API_URL}/setCoverFromStory`,
      { highlightName, storyId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response?.data;
  }
};

// Check if a highlight exists
export const checkHighlightExists = async (highlightName, token) => {
  try {
    const { data } = await axios.get(
      `${API_URL}/checkHighlightExists?highlightName=${encodeURIComponent(highlightName)}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response?.data;
  }
};

// Delete entire highlight group
export const deleteHighlightGroup = async (highlightName, token) => {
  try {
    // Encode highlightName for URL
    const encodedName = encodeURIComponent(highlightName);
    const { data } = await axios.delete(
      `${API_URL}/deleteHighlightGroup/${encodedName}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return data;
  } catch (error) {
    return error.response?.data;
  }
};

// Get current user's story archive
export const getArchiveStories = async (token) => {
  try {
    const { data } = await axios.get(`${API_URL}/getArchiveStories`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return data;
  } catch (error) {
    return error.response?.data;
  }
};

// Restore archived story to Stories
export const restoreStory = async (storyId, token, duration = 24) => {
  try {
    const { data } = await axios.put(
      `${API_URL}/restoreStory/${storyId}`,
      { duration },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
  } catch (error) {
    return error.response?.data;
  }
};

// Add archived story to Highlights
export const addToHighlightsFromArchive = async (storyId, highlightName, token) => {
  try {
    const { data } = await axios.put(
      `${API_URL}/addToHighlightsFromArchive`,
      { storyId, highlightName },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
  } catch (error) {
    return error.response?.data;
  }
};

// Permanently delete story from Archive
export const deleteFromArchive = async (storyId, token) => {
  try {
    const { data } = await axios.delete(
      `${API_URL}/deleteFromArchive/${storyId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
  } catch (error) {
    return error.response?.data;
  }
}; 
